import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { debounce } from 'lodash';

import { useSelector } from 'react-redux';
import Modal from '../../../../../../shared/design-system/components/atoms/modal';
import Select from '../../../../../../shared/design-system/components/select/select';
import { Placement } from '../../../../../../shared/design-system/components/overlay';
import { ExtendedOption } from '../../../../../../shared/design-system/components/transfer/transfer';
import { useInfiniteScroll } from '../../../../../../shared/hooks/use-infinite-scroll';
import { getDoNotCallLists } from '../../../../../settings/helpers/do-not-call.api';
import { DoNotCallList } from '../../../../../settings/types/do-not-call-list';
import { DoNotCallListsSortBy } from '../../../../../settings/enums/do-not-call-list';
import { SortOrder } from '../../../../../../shared/enums/order';
import GlobalRestrictionModal from '../../../../../home/components/global-restriction-modal';
import { RootState } from '../../../../../../store/root-reducer';

type DncListSelectOption = ExtendedOption & {
  key: string;
  label: string;
};

export type AddToDncModalProps = {
  show: boolean;
  onClose: () => void;
  /** Phone number shown as-is (no formatting applied). */
  phoneNumber: string;
  /** Called with the selected DNC list id. Parent handles the API submit. */
  onAddToDnc: (dncListId: string) => void;
  /** True while the parent is submitting the API call — disables and shows spinner on Add button. */
  isSubmitting?: boolean;
};

const PAGE_SIZE = 25;

const AddToDncModal: React.FC<AddToDncModalProps> = ({
  show,
  onClose,
  phoneNumber,
  onAddToDnc,
  isSubmitting = false,
}) => {
  const [dncLists, setDncLists] = useState<DoNotCallList[]>([]);
  const [selectedListId, setSelectedListId] = useState<string | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [requestOptions, setRequestOptions] = useState({
    search: '',
    page: 1,
    totalCount: 0,
  });
  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );
  const isDemoAccount = useSelector(
    (state: RootState) => state.home?.isDemoAccount,
  );

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const modalContainerRef = useRef<HTMLDivElement | null>(null);
  const lastSearchTermRef = useRef('');

  const fetchLists = useCallback(
    async (params: { search: string; page: number; isFirstPage: boolean }) => {
      if (params.isFirstPage) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const response = await getDoNotCallLists({
          page: params.page,
          limit: PAGE_SIZE,
          search: params.search,
          sortByKey: DoNotCallListsSortBy.UpdatedAt,
          order: SortOrder.DESC,
        });

        const results: DoNotCallList[] = response?.payload?.items ?? [];
        const total: number = response?.payload?.meta?.totalItems ?? 0;

        setDncLists((prev) =>
          params.isFirstPage ? results : [...prev, ...results],
        );
        setRequestOptions((prev) => ({ ...prev, totalCount: total }));
      } catch {
        if (params.isFirstPage) setDncLists([]);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        const trimmed = term?.trim();
        if (trimmed === lastSearchTermRef.current) return;
        lastSearchTermRef.current = trimmed;
        setDncLists([]);
        setRequestOptions((prev) => ({
          ...prev,
          search: trimmed,
          page: 1,
          totalCount: 0,
        }));
        fetchLists({ search: trimmed, page: 1, isFirstPage: true });
      }, 400),
    [fetchLists],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // Reset and load on open
  useEffect(() => {
    if (!show) return;
    setSelectedListId(undefined);
    setDncLists([]);
    lastSearchTermRef.current = '';
    setRequestOptions({ search: '', page: 1, totalCount: 0 });
    fetchLists({ search: '', page: 1, isFirstPage: true });
  }, [show, fetchLists]);

  const hasMore = dncLists.length < requestOptions.totalCount;

  const handleLoadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;
    const nextPage = requestOptions.page + 1;
    setRequestOptions((prev) => ({ ...prev, page: nextPage }));
    fetchLists({
      search: requestOptions.search,
      page: nextPage,
      isFirstPage: false,
    });
  }, [isLoading, isLoadingMore, hasMore, requestOptions, fetchLists]);

  useInfiniteScroll({
    scrollContainerRef,
    hasMore,
    onLoadMore: handleLoadMore,
    isLoadingMore: isLoading || isLoadingMore,
    enabled: show && isDropdownOpen,
    rebindKey: show && isDropdownOpen ? 1 : 0,
  });

  const selectOptions: DncListSelectOption[] = useMemo(
    () =>
      dncLists.map((list) => ({
        key: list.id,
        label: list.name,
      })),
    [dncLists],
  );

  const handleSubmit = useCallback(() => {
    if (isDemoAccount) {
      setShowGlobalRestrictionModal(true);
      return;
    }
    if (!selectedListId) return;
    onAddToDnc(selectedListId);
  }, [selectedListId, onAddToDnc]);

  const selectedList = useMemo(
    () => dncLists.find((list) => list.id === selectedListId),
    [dncLists, selectedListId],
  );

  const warningMessage = useMemo(() => {
    if (!selectedList) return null;
    if (!selectedList.client) {
      return `This number (${phoneNumber}) will be marked as Do Not Call globally (including client sequences). You won’t be able to call them, and call-related sequence tasks will not be executed.`;
    }
    const clientName = selectedList.client.companyName;
    return `This number (${phoneNumber}) will be marked as Do Not Call for ${clientName}'s sequences. You won’t be able to make calls from call tasks associated with this client’s sequences.`;
  }, [selectedList, phoneNumber]);

  const titleContent = (
    <span className="semibold-3 gray-txt-9">Add to Do Not Call (DNC) List</span>
  );

  return (
    <div ref={modalContainerRef}>
      <Modal
        show={show}
        onClose={onClose}
        onHide={onClose}
        titleContent={titleContent}
        showCloseIcon
        isCustomCloseIcon
        onSubmit={handleSubmit}
        cancelButtonText="Cancel"
        submitButtonText="Add"
        isSubmitDisabled={!selectedListId || isSubmitting}
        isSubmitLoading={isSubmitting}
        hideFooter={false}
        className="bs-add-to-dnc-modal"
        extraModalProps={{ enforceFocus: false }}
      >
        <div className="bs-add-to-dnc-modal__body">
          <div className="semibold-2 gray-txt-9 bs-add-to-dnc-modal__field-label mb-2">
            Select DNC List
          </div>
          <Select<DncListSelectOption>
            className="bs-add-to-dnc-modal__select"
            placeholder="Search or Select DNC List"
            options={selectOptions}
            placement={Placement.Bottom}
            showSearch
            searchPlaceholder="Search DNC List"
            isLoading={isLoading || isLoadingMore}
            filterOption={() => true}
            selectedOptionKey={selectedListId}
            onSearch={(value) => debouncedSearch(value)}
            onToggle={(isOpen) => setIsDropdownOpen(!!isOpen)}
            autoFocusSearch
            scrollContainerRef={scrollContainerRef}
            container={modalContainerRef}
            optionRenderer={(option) => (
              <span className="regular-2 gray-txt-9">{option.label}</span>
            )}
            onChange={([option]) => {
              if (option?.key !== undefined && option?.key !== null) {
                setSelectedListId(String(option.key));
              }
            }}
            selectedOptionRenderer={([option]) =>
              option ? (
                <span className="regular-2 gray-txt-9">{option.label}</span>
              ) : null
            }
          />
          {warningMessage && (
            <div
              className="alert alert-warning bs-add-to-dnc-modal__warning mb-0 mt-2"
              role="alert"
            >
              <p className="regular-2 gray-txt-9 mb-0 ">{warningMessage}</p>
            </div>
          )}
        </div>
      </Modal>
      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="Adding to DNC list is disabled in demo mode"
        content="This is a demo account with sample data. Signup to add to DNC list"
        primaryBtnLabel="Sign up"
      />
    </div>
  );
};

export default memo(AddToDncModal);
